/**
 * Builds an absolute URL into a sibling LanguageBird app (admin/instructor/learner) for a given
 * in-app route.
 *
 * Each app is served under a different path per environment: in production the admin app lives under
 * `/admin/` and the instructor app under `/instructor/` (so external links must keep that prefix),
 * while the learner app sits at the root and in staging each app is the root of its own subdomain.
 * The `*AppUrl` values from app-settings encode that difference, so we resolve the route relative to
 * the configured base instead of hard-coding a prefix.
 *
 * A leading slash on `route` is intentionally stripped: `new URL('/x', 'https://host/admin/')`
 * resolves to the origin root (`https://host/x`) and would silently drop the `/admin/` prefix. By
 * resolving a relative path against a base that ends in `/`, the prefix is preserved in prod and
 * absent in staging — matching how the apps are actually served.
 */
export function buildAppUrl(appUrl: string | undefined | null, route: string): string {
	// Normalize absolute routes down to path/query/hash so they re-anchor to the app base.
	let relative = route;
	if (/^https?:\/\//i.test(route)) {
		const parsed = new URL(route);
		relative = `${parsed.pathname}${parsed.search}${parsed.hash}`;
	}

	// Strip leading slashes so the route resolves relative to the app base (keeps the /admin/ prefix).
	relative = relative.replace(/^\/+/, '');

	if (!appUrl) {
		// No configured origin (e.g. local dev) — fall back to a root-relative path.
		return `/${relative}`;
	}

	const base = appUrl.endsWith('/') ? appUrl : `${appUrl}/`;
	return new URL(relative, base).toString();
}
